import { IMPORT_TARGET } from '../constants/importTargets.js';
import { ROW_STATUS } from '../constants/reviewFilters.js';

const LIST_SEPARATOR = /[;,|/]/;

const splitList = (value) =>
  String(value ?? '')
    .split(LIST_SEPARATOR)
    .map((item) => item.trim())
    .filter(Boolean);

/** First word is the first name, the rest the last name. */
const splitFullName = (full) => {
  const [first = '', ...rest] = String(full ?? '').trim().split(/\s+/);
  return { first_name: first, last_name: rest.join(' ') };
};

/**
 * Turns the raw rows of the file into review rows using the column mapping. The row keeps
 * every raw cell (`cells`) so the search covers all columns, including the ones not imported.
 * Rows come out unvalidated (`status: 'new'`, no issues) and without access
 * (`access: null`); `partitionRows` / `validateRows` fill in the rest.
 *
 * @param {{ rawRows: Array<{ rowNumber: number, cells: string[] }>, headers: string[], mapping: Record<string, string> }} input
 */
export function buildRows({ rawRows, headers, mapping }) {
  const columnOf = (target) => headers.findIndex((header) => mapping[header] === target);
  const columns = Object.fromEntries(Object.values(IMPORT_TARGET).map((t) => [t, columnOf(t)]));
  const read = (cells, target) => (columns[target] >= 0 ? (cells[columns[target]] ?? '').trim() : '');

  return rawRows.map(({ rowNumber, cells }) => {
    const names =
      columns[IMPORT_TARGET.FIRST_NAME] >= 0
        ? {
            first_name: read(cells, IMPORT_TARGET.FIRST_NAME),
            last_name: read(cells, IMPORT_TARGET.LAST_NAME),
          }
        : splitFullName(read(cells, IMPORT_TARGET.FULL_NAME));
    return {
      rowNumber,
      cells,
      person: {
        email: read(cells, IMPORT_TARGET.EMAIL).toLowerCase(),
        ...names,
        job_title: read(cells, IMPORT_TARGET.JOB_TITLE),
        location: read(cells, IMPORT_TARGET.LOCATION),
        reports_to: read(cells, IMPORT_TARGET.REPORTS_TO).toLowerCase(),
      },
      accessFromFile: {
        user_type: read(cells, IMPORT_TARGET.USER_TYPE),
        roles: splitList(read(cells, IMPORT_TARGET.ROLES)),
        groups: splitList(read(cells, IMPORT_TARGET.GROUPS)),
      },
      access: null,
      status: ROW_STATUS.NEW,
      issues: [],
      resolution: null,
      adminConfirmed: false,
      domainConfirmed: false,
    };
  });
}
